import { useRef, useState } from "react";
import { Upload, FileText, Loader2, AlertCircle, Check } from "lucide-react";

import { cn } from "../../../lib/utils";
import { Button } from "../../ui/button";
import { Textarea } from "../../ui/textarea";

const EXPECTED_COLUMNS = [
  "platform", "url", "views", "likes", "shares", "comments",
  "hook_text", "caption", "sector", "country", "tags",
  "author_handle", "thumbnail_url", "transcript", "duration_seconds", "published_at",
];

// Minimal CSV parser supporting quoted fields & escaped quotes ("")
const parseCsv = (text) => {
  const rows = [];
  let cur = [];
  let buf = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          buf += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        buf += c;
      }
      continue;
    }
    if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      cur.push(buf);
      buf = "";
    } else if (c === "\n") {
      cur.push(buf);
      rows.push(cur);
      cur = [];
      buf = "";
    } else if (c === "\r") {
      // ignore — handle CRLF
    } else {
      buf += c;
    }
  }
  if (buf.length > 0 || cur.length > 0) {
    cur.push(buf);
    rows.push(cur);
  }
  return rows.filter((r) => r.some((cell) => String(cell).trim() !== ""));
};

const toNumber = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(String(v).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : null;
};

const normalizeRow = (header, row) => {
  const obj = {};
  header.forEach((h, i) => {
    const key = String(h || "").trim().toLowerCase();
    const val = row[i] != null ? String(row[i]).trim() : "";
    if (key) obj[key] = val;
  });
  // Numeric fields
  ["views", "likes", "shares", "comments", "duration_seconds"].forEach((k) => {
    if (k in obj) obj[k] = toNumber(obj[k]) ?? 0;
  });
  // Empty fallbacks
  Object.keys(obj).forEach((k) => {
    if (obj[k] === "") obj[k] = null;
  });
  return obj;
};

const BulkImportPanel = ({ isDark, onImport, bulkAddVideos }) => {
  const [csvText, setCsvText] = useState("");
  const [parsed, setParsed] = useState([]);
  const [parseError, setParseError] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const txt = await file.text();
      setCsvText(txt);
      parse(txt);
    } catch (_err) {
      setParseError("Bestand kon niet gelezen worden.");
    }
  };

  const parse = (text) => {
    setParseError(null);
    setResult(null);
    const rows = parseCsv(text);
    if (rows.length < 2) {
      setParseError("CSV moet een header-rij en minstens 1 datarij bevatten.");
      setParsed([]);
      return;
    }
    const header = rows[0].map((h) => String(h).trim().toLowerCase());
    if (!header.includes("url")) {
      setParseError("CSV header mist verplichte kolom 'url'.");
      setParsed([]);
      return;
    }
    const data = rows.slice(1).map((r) => normalizeRow(header, r));
    // Only keep rows that have a URL
    const cleaned = data.filter((r) => r.url);
    if (cleaned.length === 0) {
      setParseError("Geen geldige rijen gevonden (elke rij heeft 'url' nodig).");
      setParsed([]);
      return;
    }
    setParsed(cleaned);
  };

  const handleImport = async () => {
    if (parsed.length === 0) return;
    setImporting(true);
    setResult(null);
    try {
      const res = await bulkAddVideos(parsed);
      setResult(res);
      if (typeof onImport === "function") onImport(res);
    } catch (e) {
      setParseError(e?.response?.data?.message || e?.message || "Import mislukt");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          size="sm"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          className="gap-1.5 h-8 text-[12px]"
        >
          <Upload className="h-3 w-3" />
          CSV-bestand kiezen
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={handleFile}
        />
        <span className={cn("text-[11px]", isDark ? "text-gray-500" : "text-gray-500")}>
          of plak hieronder
        </span>
      </div>

      <Textarea
        value={csvText}
        onChange={(e) => {
          setCsvText(e.target.value);
          if (e.target.value.trim()) parse(e.target.value);
          else setParsed([]);
        }}
        rows={6}
        className="font-mono text-[11px]"
        placeholder={`platform,url,views,likes,shares,comments,hook_text,caption,sector,country
tiktok,https://...,12000,500,30,40,"Hook tekst","Caption tekst",fitness,BE`}
      />

      <p className={cn("text-[10px]", isDark ? "text-gray-500" : "text-gray-500")}>
        Verwachte kolommen: <span className="font-mono">{EXPECTED_COLUMNS.join(", ")}</span>. Alleen <span className="font-mono">url</span> is verplicht.
      </p>

      {parseError && (
        <div
          className={cn(
            "rounded-md border p-2 text-[12px] flex items-start gap-2",
            isDark
              ? "bg-red-500/[0.08] border-red-500/20 text-red-300"
              : "bg-red-50 border-red-200 text-red-700"
          )}
        >
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
          {parseError}
        </div>
      )}

      {parsed.length > 0 && !result && (
        <div
          className={cn(
            "rounded-lg border overflow-hidden",
            isDark ? "border-white/[0.06]" : "border-gray-200"
          )}
        >
          <div
            className={cn(
              "flex items-center justify-between px-3 py-2 border-b",
              isDark ? "bg-white/[0.02] border-white/[0.06]" : "bg-gray-50 border-gray-100"
            )}
          >
            <div className="flex items-center gap-1.5">
              <FileText
                className={cn("h-3.5 w-3.5", isDark ? "text-gray-400" : "text-gray-500")}
              />
              <span className={cn("text-[12px] font-medium", isDark ? "text-gray-200" : "text-gray-800")}>
                Voorbeeld ({parsed.length} rijen)
              </span>
            </div>
          </div>
          <div className="max-h-48 overflow-auto">
            <table className="w-full text-[11px]">
              <thead className={cn(isDark ? "bg-white/[0.02]" : "bg-gray-50")}>
                <tr>
                  {["#", "platform", "url", "hook_text", "views", "engagement"].map((c) => (
                    <th
                      key={c}
                      className={cn(
                        "text-left px-2 py-1.5 font-semibold uppercase tracking-wider text-[10px]",
                        isDark ? "text-gray-500" : "text-gray-500"
                      )}
                    >
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {parsed.slice(0, 25).map((r, i) => (
                  <tr
                    key={i}
                    className={cn(
                      "border-t",
                      isDark ? "border-white/[0.04]" : "border-gray-100"
                    )}
                  >
                    <td className={cn("px-2 py-1", isDark ? "text-gray-500" : "text-gray-400")}>
                      {i + 1}
                    </td>
                    <td className={cn("px-2 py-1 capitalize", isDark ? "text-gray-300" : "text-gray-700")}>
                      {r.platform || "other"}
                    </td>
                    <td className={cn("px-2 py-1 truncate max-w-[200px]", isDark ? "text-gray-400" : "text-gray-600")}>
                      {r.url}
                    </td>
                    <td className={cn("px-2 py-1 truncate max-w-[160px]", isDark ? "text-gray-400" : "text-gray-600")}>
                      {r.hook_text || "–"}
                    </td>
                    <td className={cn("px-2 py-1", isDark ? "text-gray-300" : "text-gray-700")}>
                      {r.views ?? 0}
                    </td>
                    <td className={cn("px-2 py-1", isDark ? "text-gray-300" : "text-gray-700")}>
                      {(Number(r.likes) || 0) + (Number(r.comments) || 0) + (Number(r.shares) || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {parsed.length > 25 && (
              <div className={cn("text-center py-1.5 text-[10px]", isDark ? "text-gray-500" : "text-gray-500")}>
                en nog {parsed.length - 25} rijen…
              </div>
            )}
          </div>
        </div>
      )}

      {result && (
        <div
          className={cn(
            "rounded-md border p-3 flex items-start gap-2",
            isDark
              ? "bg-emerald-500/[0.08] border-emerald-500/20 text-emerald-300"
              : "bg-emerald-50 border-emerald-200 text-emerald-700"
          )}
        >
          <Check className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <div className="text-[12px]">
            <p className="font-semibold">
              {result.inserted} van {result.total} video's toegevoegd
            </p>
            {result.failures?.length > 0 && (
              <p className="mt-1 opacity-80">
                {result.failures.length} rijen overgeslagen.
              </p>
            )}
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <Button
          onClick={handleImport}
          disabled={parsed.length === 0 || importing}
          className="gap-1.5"
        >
          {importing && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Importeer {parsed.length > 0 && `(${parsed.length})`}
        </Button>
      </div>
    </div>
  );
};

export default BulkImportPanel;
